// Spojení se serverem: automatické znovupřipojení, heartbeat (iOS umí nechat „mrtvý“ socket otevřený)
// a nové připojení při návratu do aplikace (visibilitychange, pageshow, online).
import type { ClientMessage, ServerMessage } from "../shared/protocol.ts";

export type ConnStatus = "connecting" | "open" | "reconnecting" | "closed";

interface Handlers {
  /** Zavolá se po každém otevření socketu; musí poslat hello. */
  onOpen(send: (m: ClientMessage) => void): void;
  onMessage(m: ServerMessage): void;
  onStatus(s: ConnStatus): void;
}

const PING_EVERY_MS = 15_000;
const PONG_TIMEOUT_MS = 8_000;

export class Connection {
  private ws: WebSocket | null = null;
  private retry = 0;
  private retryTimer: number | null = null;
  private pingTimer: number | null = null;
  private pongTimer: number | null = null;
  private stopped = false;
  private everOpened = false;

  private h: Handlers;

  constructor(h: Handlers) {
    this.h = h;
    const wake = () => {
      if (this.stopped) return;
      if (!this.ws || this.ws.readyState > WebSocket.OPEN) this.connectNow();
      else if (this.ws.readyState === WebSocket.OPEN) this.ping();
    };
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && wake());
    window.addEventListener("pageshow", wake);
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
  }

  start() {
    this.stopped = false;
    this.connectNow();
  }

  /** Ukončí spojení natrvalo (např. stůl vypršel). */
  stop() {
    this.stopped = true;
    this.clearTimers();
    this.ws?.close();
    this.ws = null;
    this.h.onStatus("closed");
  }

  /** Pro testy: shodí socket, jako by vypadla síť. */
  simulateDrop() {
    this.ws?.close();
  }

  send(m: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  private connectNow() {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.clearTimers();
    const old = this.ws;
    if (old) {
      old.onclose = old.onmessage = old.onopen = old.onerror = null;
      try {
        old.close();
      } catch {
        /* nic */
      }
    }
    this.h.onStatus(this.everOpened ? "reconnecting" : "connecting");
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.everOpened = true;
      this.h.onStatus("open");
      this.h.onOpen((m) => this.send(m));
      this.schedulePing();
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.t === "pong") {
        if (this.pongTimer !== null) clearTimeout(this.pongTimer);
        this.pongTimer = null;
        return;
      }
      this.h.onMessage(msg);
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.clearTimers();
      if (ev.code === 4000) return this.stop();
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.stopped) return;
    this.h.onStatus("reconnecting");
    const delay = Math.min(5000, 500 * 2 ** this.retry);
    this.retry++;
    this.retryTimer = window.setTimeout(() => this.connectNow(), delay);
  }

  private schedulePing() {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = window.setInterval(() => this.ping(), PING_EVERY_MS);
  }

  private ping() {
    if (this.ws?.readyState !== WebSocket.OPEN || this.pongTimer !== null) return;
    this.send({ t: "ping" });
    this.pongTimer = window.setTimeout(() => {
      // Žádná odpověď: socket je mrtvý, i když tvrdí, že je otevřený.
      this.pongTimer = null;
      this.connectNow();
    }, PONG_TIMEOUT_MS);
  }

  private clearTimers() {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    if (this.pongTimer !== null) clearTimeout(this.pongTimer);
    this.pingTimer = this.pongTimer = null;
  }
}
